import React from "react";
import { Link } from "react-router-dom";
import { Shield, GraduationCap, Sprout, Heart, Leaf, ScrollText, HeartHandshake, ArrowRight } from "lucide-react";
import { PHOTO_TSHIRT, FOUNDER_PHOTO } from "@/lib/assets";
import AnthemSection from "@/components/AnthemSection";

const values = [
  { icon: Shield, t: "Protection", d: "The safety, dignity and well-being of children come first." },
  { icon: Heart, t: "Compassion", d: "Serve children, families and communities with dignity, empathy and respect." },
  { icon: HeartHandshake, t: "Empowerment", d: "Help people develop skills, resources and opportunities for stronger futures." },
  { icon: GraduationCap, t: "Education", d: "Use education, mentorship and practical skills to create opportunity." },
  { icon: Leaf, t: "Sustainability", d: "Develop lasting solutions through partnerships, employment, entrepreneurship and responsible social enterprise." },
  { icon: ScrollText, t: "Integrity & Transparency", d: "Maintain responsible governance, financial accountability and transparent reporting." },
];

const vision5yr = [
  "Establish safe, family-centred care for vulnerable children.",
  "Grow education, mentorship and practical-skills programs.",
  "Build the founding board, advisory council and ambassador network.",
  "Launch Mercy Made as a responsible, purpose-driven social enterprise.",
  "Develop community projects in water, education, agriculture and technology.",
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-16">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">About Refugium Ethiopia</p>
        <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-[#1F4D2E] leading-[0.95]">Born from loss. Built on love.</h1>
        <p className="text-lg text-[#4F6B54] mt-6 leading-relaxed">Refugium Ethiopia is a child-protection nonprofit dedicated to giving vulnerable children a safe home, a real education, and a future they can build for themselves. We began after the loss of a little girl named Mercy — and we carry her name into everything we do.</p>
        <p className="mt-6 inline-block text-sm tracking-[0.25em] uppercase font-bold text-[#1F4D2E] border border-[#1F4D2E]/20 rounded-full px-5 py-2">Protect · Educate · Empower</p>
      </section>

      {/* FOUNDER */}
      <section className="bg-[#1F4D2E] text-white" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-4 flex justify-center md:justify-start order-1">
            <img src={FOUNDER_PHOTO} alt="Amelework &ldquo;Amilly&rdquo; Kassahun, Founder of Refugium Ethiopia" data-testid="founder-avatar" className="w-48 h-48 rounded-full object-cover object-top border-4 border-[#E0A526]/40 shadow-xl mx-auto" />
          </div>
          <div className="md:col-span-8 order-2">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">Our Founder</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-tight">Amelework &ldquo;Amilly&rdquo; Kassahun</h2>
            <p className="text-[#E0A526] font-medium mt-2">Founder, Refugium Ethiopia · Addis Ababa</p>
            <p className="text-[#E7EDE2]/90 mt-6 leading-relaxed">Abandoned as an infant and facing extraordinary challenges throughout her childhood in Addis Ababa, Amilly eventually received the support and education that changed the direction of her life. She founded Refugium Ethiopia to open that same door for vulnerable girls in Ethiopia.</p>
            <Link to="/founder" data-testid="about-founder-link" className="mt-6 inline-flex items-center gap-2 bg-[#E0A526] text-[#1F4D2E] rounded-full px-6 py-3 font-semibold hover:bg-white transition-colors">Read Amilly's story <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-10" data-testid="mission-block">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our Mission</p>
          <p className="text-xl text-[#1F4D2E] leading-relaxed">To protect vulnerable children, strengthen families, expand access to education and opportunity, and empower Ethiopian communities through compassionate care, sustainable development, and economic opportunity.</p>
        </div>
        <div className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-2xl p-10" data-testid="vision-block">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our Vision</p>
          <p className="text-xl text-[#1F4D2E] leading-relaxed">An Ethiopia where children can grow in safety and dignity, families have opportunities to thrive, and communities have pathways toward sustainable economic opportunity.</p>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="bg-[#E7EDE2]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our Core Values</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">The principles that guide everything we do.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.t} className="bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-xl p-8 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300" data-testid={`value-${v.t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                <span className="w-14 h-14 rounded-full bg-[#1F4D2E]/10 flex items-center justify-center text-[#1F4D2E] mb-5"><v.icon size={26} /></span>
                <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-2">{v.t}</h3>
                <p className="text-[#4F6B54] leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="leadership-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Our Leadership</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">We are building our founding leadership now.</h2>
            <p className="text-[#4F6B54] mt-5 leading-relaxed">Refugium Ethiopia is assembling a founding board of directors, a professional advisory council, and a network of ambassadors and partners. If you bring experience in law, medicine, education, nonprofit leadership, finance, fundraising, business or community-building, we would love to hear from you.</p>
            <Link to="/join" data-testid="about-join-btn" className="mt-7 inline-flex items-center gap-2 bg-[#1F4D2E] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#C1272D] transition-colors">Join our founding network <ArrowRight size={18} /></Link>
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#1F4D2E] text-white rounded-2xl p-10">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-5">Our 5-Year Vision</p>
              <ul className="space-y-3">
                {vision5yr.map((v) => (
                  <li key={v} className="flex items-start gap-3 text-[#E7EDE2]/90"><span className="w-1.5 h-1.5 rounded-full bg-[#E0A526] mt-2 shrink-0" /> {v}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WEAR THE MISSION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center" data-testid="merch-section">
        <div className="md:col-span-5 order-2 md:order-1">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Wear the mission</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Carry Mercy's story with you.</h2>
          <p className="text-[#4F6B54] mt-5 leading-relaxed">Our "Protect · Educate · Empower" tee is a quiet way to start conversations and spread the word. Every share helps another child find a safe home.</p>
          <p className="font-display italic text-lg text-[#4F6B54] mt-5">"In Loving Memory of Mercy — Her Story Inspires Our Mission."</p>
        </div>
        <div className="md:col-span-7 order-1 md:order-2">
          <img src={PHOTO_TSHIRT} alt="Refugium Ethiopia t-shirt" className="w-full h-auto object-cover rounded-2xl" />
        </div>
      </section>

      <AnthemSection />
    </div>
  );
}
